/**
 * Dropdown component — a styled wrapper around Radix UI DropdownMenu.
 *
 * Exports the full Radix API surface with consistent cosmic styling.
 *
 * Usage:
 * ```tsx
 * <DropdownMenu>
 *   <DropdownTrigger asChild>
 *     <Button variant="ghost" size="sm">Options <ChevronDown className="w-3.5 h-3.5" /></Button>
 *   </DropdownTrigger>
 *   <DropdownContent align="end">
 *     <DropdownLabel>Actions</DropdownLabel>
 *     <DropdownItem onSelect={handleEdit}>Edit</DropdownItem>
 *     <DropdownSeparator />
 *     <DropdownItem onSelect={handleDelete} className="text-inauspicious">Delete</DropdownItem>
 *   </DropdownContent>
 * </DropdownMenu>
 * ```
 */
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { Check, ChevronRight, Circle } from 'lucide-react'
import { cn } from '@/lib/utils'

export const DropdownMenu = DropdownMenuPrimitive.Root
export const DropdownTrigger = DropdownMenuPrimitive.Trigger
export const DropdownGroup = DropdownMenuPrimitive.Group
export const DropdownPortal = DropdownMenuPrimitive.Portal
export const DropdownSub = DropdownMenuPrimitive.Sub
export const DropdownRadioGroup = DropdownMenuPrimitive.RadioGroup

// ─── Shared class strings ────────────────────────────────────────────────────

const contentBase = cn(
  'z-50 min-w-[160px] overflow-hidden rounded-xl p-1',
  'bg-cosmic-nebula ring-1 ring-white/10 backdrop-blur-md',
  'shadow-[0_8px_32px_rgba(0,0,0,0.5),0_0_0_1px_rgba(167,139,250,0.08)]',
  'data-[state=open]:animate-scale-in',
  'data-[state=closed]:animate-scale-out',
)

const itemBase = cn(
  'relative flex cursor-pointer select-none items-center gap-2',
  'rounded-lg px-2.5 py-1.5',
  'text-sm text-star-soft',
  'outline-none transition-colors duration-100',
  'focus:bg-cosmic-dusk focus:text-star-bright',
  'data-[disabled]:pointer-events-none data-[disabled]:opacity-40',
)

// ─── Content ─────────────────────────────────────────────────────────────────

export function DropdownContent({
  className,
  sideOffset = 4,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        sideOffset={sideOffset}
        className={cn(contentBase, className)}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  )
}

// ─── Items ───────────────────────────────────────────────────────────────────

export function DropdownItem({
  className,
  inset,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & { inset?: boolean }) {
  return (
    <DropdownMenuPrimitive.Item
      className={cn(itemBase, inset && 'pl-8', className)}
      {...props}
    />
  )
}

export function DropdownCheckboxItem({
  className,
  children,
  checked,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>) {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      className={cn(itemBase, 'pl-8', className)}
      checked={checked}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <Check className="w-3 h-3 text-gold" aria-hidden="true" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  )
}

export function DropdownRadioItem({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>) {
  return (
    <DropdownMenuPrimitive.RadioItem
      className={cn(itemBase, 'pl-8', className)}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <Circle className="w-2 h-2 fill-gold text-gold" aria-hidden="true" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  )
}

// ─── Structure ───────────────────────────────────────────────────────────────

export function DropdownLabel({
  className,
  inset,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & { inset?: boolean }) {
  return (
    <DropdownMenuPrimitive.Label
      className={cn(
        'px-2.5 py-1.5 text-2xs font-medium tracking-wider uppercase text-star-dim',
        inset && 'pl-8',
        className,
      )}
      {...props}
    />
  )
}

export function DropdownSeparator({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>) {
  return (
    <DropdownMenuPrimitive.Separator
      className={cn('-mx-1 my-1 h-px bg-cosmic-mist/50', className)}
      {...props}
    />
  )
}

// ─── Submenu ─────────────────────────────────────────────────────────────────

export function DropdownSubTrigger({
  className,
  inset,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & { inset?: boolean }) {
  return (
    <DropdownMenuPrimitive.SubTrigger
      className={cn(itemBase, 'justify-between', inset && 'pl-8', className)}
      {...props}
    >
      {children}
      <ChevronRight className="w-3.5 h-3.5 ml-auto text-star-dim" aria-hidden="true" />
    </DropdownMenuPrimitive.SubTrigger>
  )
}

export function DropdownSubContent({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.SubContent
        className={cn(contentBase, className)}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  )
}
